import { getCalendarLabelByMode } from '@modules/common/components/DateRangePicker/CalendarPicker/helpers';
import { getDateRange } from '@modules/common/components/DateRangePicker/helpers';
import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import { WhereClausePreview } from '@modules/common/components/WhereClausePreview';
import {
  useFetchRecordCount,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { formatNumToStandardNotation } from '@modules/common/utils';
import { FeedbackListDrawer } from '@modules/feedback/components/FeedbackListDrawer';
import { useGetEqlFieldFromEqlGroupBy } from '@modules/oracle/hooks';
import {
  getDurationFromAssistantResponseEqlQuery,
  getWhereClauseFromAssistantResponseEqlQuery,
} from '@modules/oracle/utils';
import {
  useFetchQuantifyFormulas,
  useGetSelectedMeasuredAsCriteriaOption,
} from '@modules/quantify/hooks';
import { getHasMetadataFilterForEqlField } from '@modules/quantify/utils';
import { CalendarTodayRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Stack,
  Tooltip,
  Typography,
  alpha,
  keyframes,
} from '@mui/material';
import { EqlQuery, TimeUnit } from 'generated/graphql';
import pluralize from 'pluralize';
import { useEffect, useMemo, useState } from 'react';
import { TimeRange } from 'v2/core/entities';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import {
  EmptyWhereClause,
  OperationType,
} from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';

interface WisdomConversationStepQueryDetailsProps {
  eqlQuery: EqlQuery;
  shouldAnimate?: boolean;
  onClick: (() => void) | undefined;
  responseType: 'quantify' | 'summary';
  isReadOnly: boolean;
}

export const WisdomConversationStepQueryDetails = ({
  eqlQuery,
  shouldAnimate = false,
  onClick,
  responseType,
  isReadOnly,
}: WisdomConversationStepQueryDetailsProps) => {
  const {
    tenant: { timezone },
  } = useGetUser();

  const { data: sourceDetailsMap, status } = useGetSourceDetailsMap();

  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();

  const [shouldOpenFeedbackDrawer, setShouldOpenFeedbackDrawer] =
    useState(false);

  const { selectedGroupByOption: groupBy, isLoading: isGroupByLoading } =
    useGetEqlFieldFromEqlGroupBy(eqlQuery?.properties.groupBy);

  const formulaID = eqlQuery?.properties.formula?.eqlFunction
    ? (eqlQuery.properties.formula.eqlFunction as EQLFunctionName)
    : EQLFunctionName.Count;

  const isQuantifyResponse = responseType === 'quantify';

  const {
    isLoading: isMeasuredAsCriteriaLoading,
    selectedMeasuredAsCriteriaOption,
  } = useGetSelectedMeasuredAsCriteriaOption({
    formulaID,
    groupBy,
    measuredAsCriteria: undefined,
    shouldSkip: !groupBy || !formulaID || !isQuantifyResponse,
  });

  const [fetchQuantifyFormulas, { formulas, isLoading: isLoadingFormulas }] =
    useFetchQuantifyFormulas({
      fetchPolicy: 'cache-first',
      shouldSkip: !groupBy?.field || !formulaID || !isQuantifyResponse,
    });

  useEffect(() => {
    groupBy?.field && fetchQuantifyFormulas(groupBy.field);
  }, [fetchQuantifyFormulas, groupBy?.field]);

  const formulaDisplayName = formulas
    ? formulas.find((formula) => formula.eqlFunction === formulaID)?.shortName
    : formulaID;

  const isLoading =
    isGroupByLoading ||
    isMeasuredAsCriteriaLoading ||
    IsLoading(status) ||
    isLoadingFormulas;

  const whereClause = useMemo(
    () =>
      getWhereClauseFromAssistantResponseEqlQuery({
        sourceDetailsMap,
        timezone,
        whereClause: eqlQuery.properties.whereClause,
      }),
    [eqlQuery.properties.whereClause, sourceDetailsMap, timezone],
  );

  const duration = useMemo(
    () =>
      getDurationFromAssistantResponseEqlQuery(eqlQuery.properties.duration),
    [eqlQuery],
  );

  const queryContainer = useMemo((): QueryContainer => {
    const metadataFilter = getHasMetadataFilterForEqlField(groupBy);

    const whereClauseToUse = !!metadataFilter
      ? combineWhereClauses(
          whereClause ?? EmptyWhereClause,
          metadataFilter,
          OperationType.AND,
        )
      : whereClause ?? EmptyWhereClause;

    return {
      WhereClause: whereClauseToUse,
      DateRange: duration as TimeRange,
      AggregatePeriod: TimeUnit.Week,
    };
  }, [duration, groupBy, whereClause]);

  const { data: recordCount } = useFetchRecordCount({
    queryContainer,
  });

  const [from, to] = getDateRange(duration);
  const durationString = getCalendarLabelByMode([from, to], duration, '--');

  const isClickable = !!onClick && !isLoading;

  return (
    <Stack
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        p: 1,
        width: '100%',
        minWidth: 0,
      }}
      spacing={0.5}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          divider={<Divider flexItem orientation="vertical" />}
        >
          <Tooltip
            arrow
            title={isClickable && !isReadOnly ? 'Click to edit' : null}
            placement="top"
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{
                '&:hover':
                  isClickable && !isReadOnly
                    ? {
                        cursor: 'pointer',
                      }
                    : {},
              }}
              onClick={() => {
                if (!isClickable || isReadOnly) {
                  return;
                }
                onClick();
              }}
            >
              <CalendarTodayRounded
                sx={{
                  color: 'text.secondary',
                  fontSize: '1rem',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {durationString}
              </Typography>
            </Stack>
          </Tooltip>
          {!!groupBy?.displayName && isQuantifyResponse && (
            <Typography variant="caption" color="text.secondary">
              Plotting:{' '}
              {!!groupBy.detail?.metadata
                ? getFormattedDisplayNameForEqlField({
                    eqlField: groupBy,
                    shouldRemoveFileUploadPrefix,
                  })
                : pluralize(groupBy.displayName)}
            </Typography>
          )}
          {!!formulaDisplayName && isQuantifyResponse && (
            <Typography variant="caption" color="text.secondary">
              Measured as: {formulaDisplayName} of{' '}
              {selectedMeasuredAsCriteriaOption?.displayName ||
                'Feedback Records'}
            </Typography>
          )}
        </Stack>
        {!!recordCount && (
          <Button
            size="small"
            onClick={() => setShouldOpenFeedbackDrawer(true)}
            sx={{
              textTransform: 'none',
            }}
          >
            {formatNumToStandardNotation(recordCount)}{' '}
            {pluralize('Feedback Record', recordCount)}
          </Button>
        )}
      </Stack>
      <Tooltip
        arrow
        title={`Click to ${isReadOnly ? 'inspect' : 'edit'} query`}
        placement="top"
      >
        <Box
          onClick={() => {
            if (!isClickable) {
              return;
            }
            onClick();
          }}
          sx={{
            width: '100%',
            minWidth: 0,
          }}
        >
          <WhereClausePreview
            isLoading={isLoading}
            whereClause={whereClause}
            sxProps={{
              background: (theme) => alpha(theme.palette.primary.main, 0.07),
              animation: () => {
                if (!shouldAnimate) {
                  return 'none';
                }

                const highlightKeyframes = keyframes`
                0% {
                  opacity: 0.4;
                }
                50% {
                  opacity: 1;
                }
                100% {
                  opacity: 0.4;
                }
                `;
                return `${highlightKeyframes} 2s infinite`;
              },
              border: 'none',
              '&:hover': isClickable
                ? {
                    cursor: 'pointer',
                    background: (theme) =>
                      alpha(theme.palette.primary.main, 0.15),
                  }
                : undefined,
            }}
          />
        </Box>
      </Tooltip>
      <FeedbackListDrawer
        open={shouldOpenFeedbackDrawer}
        onClose={() => setShouldOpenFeedbackDrawer(false)}
        queryContainer={queryContainer}
        header={{
          shouldEnableSort: true,
          renderTitle: () => null,
          renderDescription: () => (
            <Typography>Feedback with existing filters</Typography>
          ),
        }}
      />
    </Stack>
  );
};
